import React from 'react';

import { theme as antTheme, Descriptions, Divider, Drawer, Empty, Flex, Space, Spin, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { ReceiptDocumentSchema, WarehouseDocumentItemSchema } from '@app/models/wms';
import { formatDate, formatDateAndTimeShort } from '@app/services/date';

import { Paper } from '@components/Paper';

import { useGetDocumentQuery } from '../../../../../../hooks';
import { MappedStatusTag } from '../MappedStatusTag';

import { FileList } from './components/FileList';
import { OrderLink } from './components/OrderLink';
import { getItemColumns, renderValue } from './constants';

interface IProps {
  documentUid: string | null;
  open: boolean;
  onClose: () => void;
}

export const DocumentDetailDrawer: React.FC<IProps> = ({ documentUid, open, onClose }) => {
  const { token } = antTheme.useToken();
  const documentsT = useTranslation(NSKeys.warehouseDocuments);
  const { data, isLoading } = useGetDocumentQuery(open ? documentUid : null);

  const itemColumns = React.useMemo<ColumnsType<WarehouseDocumentItemSchema>>(
    () =>
      data?.kind
        ? getItemColumns({ kind: data.kind }).map((column) => ({
            ...column,
            title: documentsT.t(`items.${String(column.key)}` as any),
          }))
        : [],
    [data, documentsT, documentUid]
  );

  return (
    <Drawer
      title={documentsT.t('drawerTitle')}
      width={720}
      open={open}
      onClose={onClose}
      destroyOnClose
      styles={{ body: { backgroundColor: token.colorBgLayout } }}>
      {isLoading || !data ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin />
        </div>
      ) : (
        <Flex vertical={true}>
          <Divider titlePlacement="left">{documentsT.t('section.general')}</Divider>
          <Paper>
            <Descriptions column={1} size="small" bordered>
              {data?.kind === 'shipment' && data?.order_uid && (
                <Descriptions.Item label={documentsT.t('order.label')}>
                  <OrderLink orderUid={data.order_uid} />
                </Descriptions.Item>
              )}
              <Descriptions.Item label={documentsT.t('tableColumns.number')}>
                {renderValue(data.number)}
              </Descriptions.Item>
              <Descriptions.Item label={documentsT.t('tableColumns.external_uid')}>
                {renderValue(data.external_uid)}
              </Descriptions.Item>
              <Descriptions.Item label={documentsT.t('tableColumns.dt_document')}>
                {formatDate(data.dt_document)}
              </Descriptions.Item>
              {data.kind === 'receipt' && (
                <>
                  <Descriptions.Item label={documentsT.t('tableColumns.dt_actual_delivery')}>
                    {formatDateAndTimeShort(data?.dt_actual_delivery)}
                  </Descriptions.Item>
                  <Descriptions.Item label={documentsT.t('tableColumns.dt_placement')}>
                    {formatDateAndTimeShort(data?.dt_placement)}
                  </Descriptions.Item>
                  <Descriptions.Item label={documentsT.t('tableColumns.dt_planned_delivery')}>
                    {formatDateAndTimeShort(data?.dt_planned_delivery)}
                  </Descriptions.Item>
                </>
              )}
              <Descriptions.Item label={documentsT.t('tableColumns.mapped_status')}>
                <MappedStatusTag status={data.mapped_status} rawStatus={data.raw_status} />
              </Descriptions.Item>
              <Descriptions.Item label={documentsT.t('tableColumns.sender')}>
                {renderValue(data.sender)}
              </Descriptions.Item>
              <Descriptions.Item label={documentsT.t('tableColumns.recipient')}>
                {renderValue(data.recipient)}
              </Descriptions.Item>
              <Descriptions.Item label={documentsT.t('tableColumns.weight')}>
                {renderValue(data.weight)}
              </Descriptions.Item>
              <Descriptions.Item label={documentsT.t('tableColumns.volume')}>
                {renderValue(data.volume)}
              </Descriptions.Item>
              <Descriptions.Item label={documentsT.t('tableColumns.places')}>
                {data.places && data.places.length ? (
                  <Space size={[4, 4]} wrap>
                    {data.places.map((place, index) => (
                      <Tag key={`${place.type}-${index}`}>
                        {place.type} {place.amount}
                      </Tag>
                    ))}
                  </Space>
                ) : (
                  '—'
                )}
              </Descriptions.Item>
              {data.kind === 'receipt' && (
                <Descriptions.Item label={documentsT.t('tableColumns.purchase_order_number')}>
                  {renderValue((data as ReceiptDocumentSchema).purchase_order_number)}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Paper>

          <Divider titlePlacement="left">{documentsT.t('section.items')}</Divider>
          <Paper>
            <Table<WarehouseDocumentItemSchema>
              size="small"
              rowKey={(_, index) => String(index)}
              columns={itemColumns}
              dataSource={data.items ?? []}
              pagination={false}
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: <Empty description={documentsT.t('items.empty')} /> }}
            />
          </Paper>

          <FileList files={data.files ?? []} />
        </Flex>
      )}
    </Drawer>
  );
};
